import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Phone, Mail, Globe, Loader2, CheckCheck, Clock, UserPlus, Pencil, Trash2, CalendarCheck, ArrowUp, ArrowDown, ArrowUpDown, RotateCcw } from "lucide-react";
import { LeadsMap } from "../LeadsMap";
import { PraxisKanban } from "./PraxisKanban";
import { STATUS_OPTIONS, STATUS_STYLE, fmtDate } from "../poolConstants";

export function PoolDetailResults({ loading, view, leads, searchCenters, onChangeStatus, onConvert, onRemove, onEdit, deletedMode = false, onRestore }) {
  // Client-seitige Sortierung nach Umkreis oder Distanz: null -> asc -> desc -> null
  const [sort, setSort] = useState(null); // { key: 'radius'|'distance', dir }

  const toggleSort = (key) =>
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return null;
    });

  const sortIcon = (key) =>
    sort?.key === key
      ? (sort.dir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />)
      : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />;

  const sortedLeads = useMemo(() => {
    if (!sort) return leads;
    const field = sort.key === "distance" ? "distance_km" : "source_radius_km";
    const arr = [...leads];
    arr.sort((a, b) => {
      const va = a[field], vb = b[field];
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // ohne Wert immer ans Ende
      if (vb == null) return -1;
      return sort.dir === "asc" ? va - vb : vb - va;
    });
    return arr;
  }, [leads, sort]);

  return (
    <Card>
      <CardContent className={view === "table" ? "p-0 overflow-x-auto" : "p-0"}>
        {loading ? (
          <div className="py-16 text-center text-muted-foreground"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : view === "map" ? (
          <div className="p-3"><LeadsMap leads={leads} searches={searchCenters} /></div>
        ) : view === "kanban" ? (
          <PraxisKanban leads={leads} onChangeStatus={onChangeStatus} onEdit={onEdit} deletedMode={deletedMode} />
        ) : leads.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground" data-testid="leads-empty">
            {deletedMode
              ? "Der Papierkorb ist leer."
              : "Keine Einträge. Klicken Sie auf „Adressen suchen\", um den Pool zu füllen."}
          </div>
        ) : (
          <Table data-testid="leads-table">
            <TableHeader>
              <TableRow>
                <TableHead>Praxis</TableHead>
                <TableHead>Adresse</TableHead>
                <TableHead>
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    Herkunft
                    <button type="button" onClick={() => toggleSort("radius")}
                      className="inline-flex items-center gap-0.5 hover:text-foreground transition-colors text-xs"
                      title="Nach Umkreis sortieren" data-testid="sort-radius-btn">
                      Umkreis {sortIcon("radius")}
                    </button>
                    <button type="button" onClick={() => toggleSort("distance")}
                      className="inline-flex items-center gap-0.5 hover:text-foreground transition-colors text-xs"
                      title="Nach Entfernung sortieren (nächste zuerst)" data-testid="sort-distance-btn">
                      Distanz {sortIcon("distance")}
                    </button>
                  </div>
                </TableHead>
                <TableHead>Kontakt</TableHead>
                <TableHead>Web</TableHead>
                <TableHead className="text-right">Bewertung</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Bearbeitet</TableHead>
                <TableHead className="sticky right-0 bg-background text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedLeads.map((l) => (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                  <TableCell className="max-w-[220px]">
                    <div className="font-medium truncate">{l.practice_name}</div>
                    <div className="flex items-center gap-1 flex-wrap mt-0.5">
                      {l.is_new && <Badge className="text-[10px] bg-blue-600 hover:bg-blue-600" data-testid={`lead-new-badge-${l.id}`}>Neu</Badge>}
                      {l.is_existing_customer && <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" data-testid={`lead-customer-badge-${l.id}`}>Bestandskunde</Badge>}
                      {l.converted_customer_id && <Badge variant="secondary" className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">übernommen</Badge>}
                      {l.practice_type && l.practice_type !== "Einzelpraxis" && <span className="text-[10px] text-muted-foreground">{l.practice_type}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm max-w-[180px]">
                    {l.street && <div className="truncate">{l.street}</div>}
                    <div className="text-muted-foreground">{[l.zip, l.city].filter(Boolean).join(" ") || "–"}</div>
                    {l.address_verified && (
                      <Badge variant="secondary" className="text-[10px] mt-0.5 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" data-testid={`lead-addr-verified-${l.id}`}>
                        <CheckCheck className="h-2.5 w-2.5 mr-0.5" /> geprüft
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    <div className="truncate max-w-[140px]" title={(l.source_locations || []).join(", ")}>{l.source_location || "–"}</div>
                    {l.source_radius_km != null && (
                      <Badge variant="outline" className="text-[10px] mt-0.5 font-normal" data-testid={`lead-radius-${l.id}`}>
                        Umkreis {l.source_radius_km} km
                      </Badge>
                    )}
                    {l.distance_km != null && (
                      <div
                        className={l.distance_km < 20 ? "mt-0.5 font-semibold text-emerald-600 dark:text-emerald-400" : "text-muted-foreground mt-0.5"}
                        data-testid={`lead-distance-${l.id}`}
                      >
                        {l.distance_km < 20 && "★ "}{l.distance_km} km entfernt
                      </div>
                    )}
                    <div className="text-muted-foreground">seit {fmtDate(l.first_seen_at)}</div>
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {l.phone
                      ? <a href={`tel:${l.phone}`} className="text-blue-600 hover:underline flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{l.phone}</a>
                      : <span className="text-muted-foreground text-xs">–</span>}
                    {l.email && <a href={`mailto:${l.email}`} className="text-blue-600 hover:underline flex items-center gap-1 text-xs mt-0.5"><Mail className="h-3 w-3" />{l.email}</a>}
                    {l.opening_hours && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 max-w-[200px]" title={l.opening_hours} data-testid={`lead-hours-${l.id}`}>
                        <Clock className="h-3 w-3 shrink-0" /><span className="truncate">{l.opening_hours}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {l.website
                      ? <a href={l.website} target="_blank" rel="noreferrer" className="text-blue-600" title={l.website}><Globe className="h-4 w-4" /></a>
                      : <span className="text-muted-foreground text-xs">–</span>}
                    {l.website_skipped && (
                      <Badge variant="secondary" className="text-[9px] mt-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 block w-fit" title="Website reagiert wiederholt nicht – Prüfung übersprungen" data-testid={`lead-web-skipped-${l.id}`}>
                        übersprungen
                      </Badge>
                    )}
                    {l.doctolib_calendar && (
                      l.doctolib_url ? (
                        <a href={l.doctolib_url} target="_blank" rel="noreferrer" className="block w-fit mt-0.5" title={`Doctolib-Terminkalender: ${l.doctolib_url}`} data-testid={`lead-doctolib-${l.id}`}>
                          <Badge className="text-[9px] bg-sky-600 hover:opacity-90 text-white flex items-center gap-0.5">
                            <CalendarCheck className="h-2.5 w-2.5" /> Doctolib
                          </Badge>
                        </a>
                      ) : (
                        <Badge className="text-[9px] mt-0.5 bg-sky-600 text-white flex items-center gap-0.5 w-fit" title="Doctolib-Terminkalender erkannt" data-testid={`lead-doctolib-${l.id}`}>
                          <CalendarCheck className="h-2.5 w-2.5" /> Doctolib
                        </Badge>
                      )
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold">{l.lead_score}</TableCell>
                  <TableCell>
                    <Select value={l.status} onValueChange={(v) => onChangeStatus(l, v)} disabled={deletedMode}>
                      <SelectTrigger className={`h-8 w-36 text-xs ${STATUS_STYLE[l.status] || ""}`} data-testid={`lead-status-${l.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                    {deletedMode ? (
                      <div data-testid={`lead-deleted-info-${l.id}`}>
                        <div>🗑 gelöscht {fmtDate(l.deleted_at)}</div>
                        {l.deleted_by && <div>von {l.deleted_by.split("@")[0]}</div>}
                        {l.deleted_at && (
                          <div className="text-amber-600" data-testid={`lead-purge-info-${l.id}`}>
                            {(() => { const d = Math.max(0, 30 - Math.floor((Date.now() - new Date(l.deleted_at).getTime()) / 86400000)); return d === 0 ? "wird heute endgültig gelöscht" : `endgültig in ${d} ${d === 1 ? "Tag" : "Tagen"}`; })()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {l.contacted_by && <div title="kontaktiert von">📞 {l.contacted_by.split("@")[0]}</div>}
                        {l.last_edited_by && <div title="zuletzt bearbeitet von">✎ {l.last_edited_by.split("@")[0]}</div>}
                        {!l.contacted_by && !l.last_edited_by && "–"}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="sticky right-0 bg-background shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                    <div className="flex items-center gap-1 justify-end">
                      {deletedMode ? (
                        <Button variant="outline" size="sm" className="text-green-700 hover:text-green-800 border-green-300"
                                onClick={() => onRestore(l)} data-testid={`lead-restore-${l.id}`}>
                          <RotateCcw className="h-4 w-4 mr-1" /> Wiederherstellen
                        </Button>
                      ) : (
                        <>
                          <Button variant="ghost" size="sm" title="Bearbeiten" onClick={() => onEdit(l)} data-testid={`lead-edit-${l.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-green-700 hover:text-green-800" title="Als Interessent übernehmen"
                                  disabled={!!l.converted_customer_id} onClick={() => onConvert(l)} data-testid={`lead-convert-${l.id}`}>
                            <UserPlus className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onRemove(l)} data-testid={`lead-delete-${l.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
